import { IFaireFundedPromo } from "@faire/web-api/indigofair/data/IFaireFundedPromo";
import { useCurrencyStore } from "@faire/web/common/currency/CurrencyStore";
import { moneyOrZero } from "@faire/web/common/money";
import { useState } from "@faire/web/ui/hooks/useState";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useSettingLIBPReactQuery } from "@faire/retailer/settings/useSettingLIBPReactQuery";
import { useProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

import { useMaybeFetchBrandView } from "../../hooks/useMaybeFetchBrandView";

export const useBrandCartHeader = (brandToken: string) => {
  const {
    isNewOrMigratedCanadian,
    displayCurrency,
    isQualifiedForEuropeFreeShipping,
  } = useRetailerStore([
    "isNewOrMigratedCanadian",
    "displayCurrency",
    "isQualifiedForEuropeFreeShipping",
  ]);
  const [
    isBrandEligibleForUkEuBrandsFreeShipping,
    setIsBrandEligibleForUkEuBrandsFreeShipping,
  ] = useState<boolean>(false);

  const { setCurrency } = useCurrencyStore(["setCurrency"]);
  const { fetchBrandView } = useProductCacheStore(["fetchBrandView"]);

  useEffect(() => {
    // TODO(bocong.zhao@faire.com, 2024/02/14): Remove when we relaunch Canada.
    if (isNewOrMigratedCanadian) {
      setCurrency(undefined);
    }
  }, [isNewOrMigratedCanadian, setCurrency]);

  const libpReactQuery = useSettingLIBPReactQuery();

  // Disabling this eslint rule because `libpReactQuery` is a constant value that does not change during the component's lifecycle.
  // This makes the conditional hook logic safe to use. This is to avoid initializing both query instances unnecessarily,
  // which would add performance overhead. This will be removed once the `LIBP_REACT_QUERY_MIGRATION_240730` XP is complete.
  const { data } = libpReactQuery
    ? // eslint-disable-next-line react-compiler/react-compiler, react-hooks/rules-of-hooks
      useMaybeFetchBrandView(brandToken ?? "")
    : // eslint-disable-next-line react-compiler/react-compiler, react-hooks/rules-of-hooks
      useQuery({
        queryKey: ["ProductCacheStore", "fetchBrandView", brandToken],
        queryFn: () => fetchBrandView(brandToken),
      });

  const brand = data?.brand;

  const faireFundedPromos: IFaireFundedPromo[] = data?.faire_funded_promo ?? [];

  const brandMinimumAmount = moneyOrZero(
    brand?.minimum_order_amount,
    displayCurrency
  );
  const showVerticalImagery = brand?.is_apparel ?? false;

  const fetchFreeShippingStatus = (retailerToken?: string) => {
    if (!retailerToken) {
      return;
    }
    setIsBrandEligibleForUkEuBrandsFreeShipping(
      data?.eligible_for_uk_eu_brands_free_shipping ?? false
    );
  };

  const hasUkEuBrandsFreeShipping =
    isQualifiedForEuropeFreeShipping &&
    isBrandEligibleForUkEuBrandsFreeShipping;

  return {
    brand,
    firstOrder: brand?.is_first_order ?? false,
    brandMinimumAmount,
    brandName: brand?.name,
    showVerticalImagery,
    faireFundedPromos,
    fetchFreeShippingStatus,
    hasUkEuBrandsFreeShipping,
  };
};
